<template>
  <div class="profile-page">
    <b-modal
      id="modalEditPassword"
      v-model="modalEditPassword"
      cancel-variant="outline-secondary"
      hide-footer
      centered
      title="Change Password"
    >
      <b-form @submit.prevent="submitEditPassword">
        <b-form-group
          label="Current Password"
          label-for="oldPassword"
        >
          <b-form-input
            id="oldPassword"
            v-model="oldPassword"
            type="password"
            placeholder="Enter current password"
          />
        </b-form-group>

        <b-form-group
          label="New Password"
          label-for="newPassword"
        >
          <div class="input-group">
            <b-form-input
              id="newPassword"
              v-model="newPassword"
              :type="passwordField"
              placeholder="Enter new password"
            />
            <b-input-group-append is-text>
              <feather-icon
                class="cursor-pointer"
                :icon="passwordToggleIcon"
                @click="togglePasswordVis"
              />
            </b-input-group-append>
          </div>
        </b-form-group>

        <b-form-group
          label="Confirm New Password"
          label-for="confirmNewPassword"
        >
          <div class="input-group">
            <b-form-input
              id="confirmNewPassword"
              v-model="confirmNewPassword"
              :type="passwordFieldConfirm"
              placeholder="Confirm new password"
            />
            <b-input-group-append is-text>
              <feather-icon
                class="cursor-pointer"
                :icon="passwordToggleIconConfirm"
                @click="togglePasswordVisConfirm"
              />
            </b-input-group-append>
          </div>
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button
            type="button"
            variant="outline-secondary"
            class="mr-1"
            @click="modalEditPassword = false"
          >
            Cancel
          </b-button>
          <b-button
            type="submit"
            variant="primary"
          >
            Update Password
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-row>
      <b-col cols="12">
        <b-card
          no-body
          class="profile-hero"
        >
          <div class="profile-hero__body">
            <div class="profile-identity">
              <b-avatar
                size="72"
                variant="light-primary"
                class="profile-avatar"
              >
                {{ profileInitials }}
              </b-avatar>
              <div class="profile-identity__text">
                <h2>{{ Name || '-' }}</h2>
                <div class="profile-meta">
                  <span>
                    <feather-icon
                      icon="BriefcaseIcon"
                      size="15"
                    />
                    {{ Occupation || 'No occupation set' }}
                  </span>
                  <span>
                    <feather-icon
                      icon="MapPinIcon"
                      size="15"
                    />
                    {{ branchName || 'No branch assigned' }}
                  </span>
                </div>
              </div>
            </div>

            <div class="profile-actions">
              <b-badge
                variant="light-primary"
                class="role-badge"
              >
                {{ roleName || 'User' }}
              </b-badge>
              <b-button
                variant="outline-primary"
                @click="modalEditPassword = true"
              >
                <feather-icon
                  icon="LockIcon"
                  size="16"
                  class="mr-50"
                />
                Change Password
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-form @submit.prevent="submitUpdateProfile">
      <b-row>
        <b-col
          cols="12"
          lg="8"
        >
          <b-card
            title="Account Information"
            class="profile-card"
          >
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  label="Full Name"
                  label-for="fullname"
                >
                  <b-form-input
                    id="fullname"
                    v-model="Name"
                    placeholder="Full name"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  label="Phone"
                  label-for="phone"
                >
                  <b-form-input
                    id="phone"
                    v-model="Phone"
                    placeholder="Phone number"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <b-form-group
                  label="Occupation"
                  label-for="occupation"
                >
                  <b-form-input
                    id="occupation"
                    v-model="Occupation"
                    placeholder="Occupation"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col
          cols="12"
          lg="4"
        >
          <b-card
            title="Signature"
            class="profile-card"
          >
            <div class="signature-preview">
              <b-img
                v-if="Signature"
                :src="Signature"
                rounded
                alt="Signature"
              />
              <div
                v-else
                class="signature-empty"
              >
                <feather-icon
                  icon="Edit3Icon"
                  size="24"
                />
                <span>No signature uploaded</span>
              </div>
            </div>

            <b-form-group
              label="Upload Signature"
              label-for="signature"
              class="mb-0"
            >
              <b-form-file
                id="signature"
                ref="uploadFile"
                placeholder="Choose file or drop it here"
                drop-placeholder="Drop file here"
                @change="handleFilesUpload"
              />
            </b-form-group>
          </b-card>
        </b-col>

        <b-col cols="12">
          <div class="profile-submit">
            <b-button
              type="submit"
              variant="primary"
            >
              <feather-icon
                icon="SaveIcon"
                size="16"
                class="mr-50"
              />
              Save Changes
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormFile,
  BFormGroup,
  BFormInput,
  BImg,
  BInputGroupAppend,
  BModal,
  BRow,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import moment from 'moment-timezone'
import { hmacSha256Hex } from '@/libs/hmacSha256'
import apiEndpoints from '@/constants/apiEndpoints'

export default {
  components: {
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormFile,
    BFormGroup,
    BFormInput,
    BImg,
    BInputGroupAppend,
    BModal,
    BRow,
  },
  data() {
    return {
      Name: '',
      Phone: '',
      Occupation: '',
      Signature: '',
      branchName: '',
      roleName: '',
      file: [],
      modalEditPassword: false,
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      passwordField: 'password',
      passwordFieldConfirm: 'password',
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordField !== 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    passwordToggleIconConfirm() {
      return this.passwordFieldConfirm !== 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    profileInitials() {
      return (this.Name || 'U')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
  },
  mounted() {
    this.getDetails()
  },
  methods: {
    togglePasswordVis() {
      this.passwordField = this.passwordField === 'password' ? 'text' : 'password'
    },
    togglePasswordVisConfirm() {
      this.passwordFieldConfirm = this.passwordFieldConfirm === 'password' ? 'text' : 'password'
    },
    getDetails() {
      const userData = this.$cookies.get('userData') || {}
      this.Name = userData.user_fullname || ''
      this.Phone = userData.user_phone || ''
      this.Occupation = userData.user_occupation || ''
      this.branchName = userData.branch ? userData.branch.branch_name : ''
      this.roleName = userData.UserRoleName || userData.role?.role_name || ''
      this.Signature = userData.user_signature ? `${process.env.VUE_APP_API_URL}upload/${userData.user_signature}` : ''
    },
    submitEditPassword() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }

      if (this.oldPassword === '' || this.newPassword === '') {
        this.toast('Please fill all password fields', 'AlertTriangleIcon', 'danger')
        return
      }

      if (this.newPassword !== this.confirmNewPassword) {
        this.toast('Your password does not match', 'AlertTriangleIcon', 'danger')
        return
      }

      const payload = {
        oldPassword: hmacSha256Hex(process.env.VUE_APP_KEY, this.oldPassword),
        newPassword: hmacSha256Hex(process.env.VUE_APP_KEY, this.newPassword),
        time: moment(),
      }

      axios
        .post(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_EDIT_PASSWORD_USER}`, payload, { headers })
        .then(response => {
          if (response.data.success === true) {
            this.modalEditPassword = false
            this.oldPassword = ''
            this.newPassword = ''
            this.confirmNewPassword = ''
            this.toast('Change Password Success', 'CheckIcon', 'success')
          } else {
            this.toast(response.data.message, 'AlertTriangleIcon', 'danger')
          }
        })
    },
    submitUpdateProfile() {
      const userToken = this.$cookies.get('userToken')
      const headers = {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${userToken}`,
      }
      const formData = new FormData()
      if (this.file && this.file[0]) {
        formData.append('file', this.file[0], this.file[0].name)
      }
      formData.append('user_fullname', this.Name)
      formData.append('user_phone', this.Phone)
      formData.append('user_occupation', this.Occupation)
      formData.append('time', moment())

      axios
        .post(`${process.env.VUE_APP_API_URL}${apiEndpoints.API_UPDATE_USER}`, formData, { headers })
        .then(response => {
          if (response.data.success === true) {
            this.$cookies.set('userData', response.data.data)
            this.getDetails()
            this.toast('Update Profile Success', 'CheckIcon', 'success')
          } else {
            this.toast('Update Profile Failed', 'AlertTriangleIcon', 'danger')
          }
        })
        .catch(() => {
          this.toast('Update Profile Failed', 'AlertTriangleIcon', 'danger')
        })
    },
    handleFilesUpload(e) {
      this.file = e.target.files || e.dataTransfer.files
    },
    toast(title, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.profile-page {
  .profile-hero {
    border: 0;
    border-radius: 8px;
    overflow: hidden;
  }

  .profile-hero__body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.5rem;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 55%, #eef7f4 100%);
    border-bottom: 1px solid rgba(34, 41, 47, 0.06);
  }

  .profile-identity {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .profile-avatar {
    flex: 0 0 auto;
    font-weight: 700;
    font-size: 1.35rem;
    box-shadow: 0 8px 22px rgba(115, 103, 240, 0.18);
  }

  .profile-identity__text {
    min-width: 0;
    margin-left: 1rem;

    h2 {
      margin-bottom: 0.35rem;
      font-size: 1.35rem;
      font-weight: 700;
      color: #2f3542;
    }
  }

  .profile-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
    color: #6e6b7b;
    font-size: 0.9rem;

    span {
      display: inline-flex;
      align-items: center;
      min-width: 0;

      svg {
        margin-right: 0.35rem;
      }
    }
  }

  .profile-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .role-badge {
    padding: 0.5rem 0.75rem;
    font-weight: 600;
  }

  .profile-card {
    border-radius: 8px;
  }

  .signature-preview {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 132px;
    margin-bottom: 1rem;
    border: 1px dashed #d8d6de;
    border-radius: 8px;
    background: #fbfbfc;

    img {
      max-width: 100%;
      max-height: 104px;
      object-fit: contain;
    }
  }

  .signature-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #6e6b7b;

    svg {
      margin-bottom: 0.5rem;
    }
  }

  .profile-submit {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
  }
}

@media (max-width: 767.98px) {
  .profile-page {
    .profile-hero__body {
      align-items: flex-start;
      flex-direction: column;
    }

    .profile-actions {
      justify-content: flex-start;
      margin-top: 1rem;
      width: 100%;
    }
  }
}
</style>
